import { r as createLogger } from "../logger-BU8YqMGU.js";
//#region ../src/sensors/absolute-orientation.ts
/**
* AbsoluteOrientationSensor capture (Generic Sensor API).
*
* Phase 1 of the AbsoluteOrientationSensor plan
* (GpsPlusSlamJs_Docs/docs/2026-06-25-0543-absolute-orientation-sensor-plan.md §5.1):
* passive instrumentation that fuses accelerometer + gyroscope + magnetometer
* into an Earth-referenced (ENU) quaternion, captured per GPS event as an
* independent north reference. No production behaviour change.
*
* Platform: Chrome Android only. Safari / Firefox / desktop / iOS lack the
* sensor → this module degrades to a clean, reported no-op.
*
* The raw `'device'` reference frame is recorded together with the screen angle
* so either the device- or screen-compensated form can be reconstructed offline
* (plan §8 Q1).
*/
const log = createLogger("AbsCompass");
/** Sensor frequency (Hz). We only snapshot at GPS rate, so low is fine (plan §8 Q4). */
const SENSOR_FREQUENCY_HZ = 20;
let sensor = null;
let latest = null;
let watchGeneration = 0;
function getSensorCtor() {
	if (typeof window === "undefined") return null;
	const ctor = window.AbsoluteOrientationSensor;
	return typeof ctor === "function" ? ctor : null;
}
/** True when the sensor can plausibly run here (secure context + API present). */
function isAbsoluteOrientationAvailable() {
	return typeof window !== "undefined" && window.isSecureContext === true && getSensorCtor() !== null;
}
function currentScreenAngleDeg() {
	if (typeof screen === "undefined") return 0;
	const angle = screen.orientation?.angle;
	return typeof angle === "number" ? angle : 0;
}
/**
* Whether all three underlying sensors are granted-or-promptable (plan §5.1).
* Returns `true` when the Permissions API is absent (best-effort — let the
* sensor constructor surface any real denial), and treats a failed individual
* query as `'granted'` for the same reason.
*/
async function sensorPermissionsUsable() {
	const permissions = navigator.permissions;
	if (!permissions || typeof permissions.query !== "function") return true;
	const queryState = async (name) => {
		try {
			return (await permissions.query({ name })).state;
		} catch {
			return "granted";
		}
	};
	return (await Promise.all([
		"accelerometer",
		"gyroscope",
		"magnetometer"
	].map(queryState))).every((s) => s === "granted" || s === "prompt");
}
/**
* Start capturing absolute orientation. Idempotent (stops any prior watch first).
*
* On platforms without the sensor — or when permissions are denied — it reports
* `unavailable`/`error` via `onStatus` and stays a no-op; the recorder keeps
* working unchanged. Never throws.
*/
async function startAbsoluteOrientationWatch(onStatus = () => {}) {
	stopAbsoluteOrientationWatch();
	const myGeneration = watchGeneration;
	const reportStatus = (status) => {
		try {
			onStatus(status);
		} catch (err) {
			log.error("onStatus callback threw; continuing", err);
		}
	};
	const Ctor = getSensorCtor();
	if (!isAbsoluteOrientationAvailable() || Ctor === null) {
		reportStatus({
			state: "unavailable",
			reason: "no AbsoluteOrientationSensor / insecure context"
		});
		return;
	}
	try {
		if (!await sensorPermissionsUsable()) {
			reportStatus({
				state: "unavailable",
				reason: "sensor permission denied"
			});
			return;
		}
		if (myGeneration !== watchGeneration) return;
		const created = new Ctor({
			frequency: SENSOR_FREQUENCY_HZ,
			referenceFrame: "device"
		});
		sensor = created;
		created.addEventListener("reading", () => {
			if (created !== sensor) return;
			const q = created.quaternion;
			if (!q || q.length < 4) return;
			const x = q[0];
			const y = q[1];
			const z = q[2];
			const w = q[3];
			if (x === void 0 || y === void 0 || z === void 0 || w === void 0) return;
			latest = {
				quaternion: [
					x,
					y,
					z,
					w
				],
				referenceFrame: "device",
				screenAngleDeg: currentScreenAngleDeg(),
				timestamp: Date.now()
			};
		});
		created.addEventListener("activate", () => {
			if (created !== sensor) return;
			log.info("active");
			reportStatus({ state: "active" });
		});
		created.addEventListener("error", (event) => {
			if (created !== sensor) return;
			latest = null;
			const reason = event?.error?.name ?? "SensorError";
			log.error("sensor error:", reason);
			reportStatus({
				state: "error",
				reason
			});
		});
		created.start();
	} catch (err) {
		sensor = null;
		latest = null;
		reportStatus({
			state: "error",
			reason: err?.name ?? String(err)
		});
	}
}
/** Latest reading, or null when unavailable/not-yet-warmed-up. Mirrors getLastDeviceOrientation. */
function getLatestAbsoluteOrientation() {
	return latest;
}
/** Stop the watch and clear cached state. Idempotent. */
function stopAbsoluteOrientationWatch() {
	watchGeneration++;
	if (sensor) {
		try {
			sensor.stop();
		} catch {}
		sensor = null;
	}
	latest = null;
}
//#endregion
export { getLatestAbsoluteOrientation, isAbsoluteOrientationAvailable, startAbsoluteOrientationWatch, stopAbsoluteOrientationWatch };
